import type { EntityFormField } from '@/components/EntityFormModal';
import type { AdminPermission } from '@wanjianshi/shared-contracts';

export interface ResourceMutationDefinition {
  permission: AdminPermission;
  title: string;
  buttonLabel: string;
  method: 'post' | 'put';
  path: string | ((row: Record<string, unknown>) => string);
  fields: EntityFormField[];
  reasonRequired?: boolean;
}

export interface ResourceDefinition {
  labels?: Record<string, string>;
  create?: ResourceMutationDefinition;
  edit?: ResourceMutationDefinition;
}

const statusOptions = [
  { label: '正常/启用', value: 0 },
  { label: '停用', value: 1 },
];

const commonLabels: Record<string, string> = {
  id: 'ID', name: '名称', title: '标题', username: '用户名', nickname: '昵称',
  user_id: '用户 ID', owner_user_id: '所有者 ID', subject_id: '主体 ID', subject_name: '主体名称',
  mobile_masked: '手机号', email_masked: '邮箱', dept_id: '部门 ID', dept_name: '部门',
  inviter: '邀请人', inviter_id: '邀请人 ID', invitee_count: '被邀请人数',
  role_codes: '后台角色', code: '编码', permission: '权限编码', type: '类型', type_name: '类型名称',
  data_scope: '数据范围', data_scope_dept_ids: '数据范围部门', parent_id: '上级 ID', leader_user_id: '负责人 ID',
  sort: '排序', status: '状态', enabled: '启用', is_active: '启用', visible: '可见', remark: '备注', memo: '说明',
  create_time: '创建时间', update_time: '更新时间', created_at: '创建时间', updated_at: '更新时间',
  client_id: '客户端', expires_time: '过期时间', result: '结果', user_ip: 'IP 地址',
  request_url: '请求地址', success: '成功', sub_type: '子类型',
  plan_id: '套餐 ID', plan_name: '套餐', membership_scope: '适用域', billing_cycle: '计费周期',
  started_at: '开始时间', expires_at: '到期时间', credits_total: '总积分', credits_used: '已用积分',
  member_count: '当前成员数', seat_count: '购买席位数', max_members: '有效席位上限', subscription_id: '会员记录 ID', subscription_status: '订阅状态', project_count: '项目数',
  founder_user_id: '所属人 ID', founder_username: '所属人账号', founder_nickname: '所属人昵称',
  founder_mobile_masked: '所属人手机号', team_status: '团队状态',
  total_credits: '团队积分', frozen_credits: '冻结积分', used_credits: '已用积分',
  project_id: '项目 ID', project_type: '项目类型', team_id: '团队 ID', team_name: '团队', canvas_count: '画布数', task_count: '任务数',
  task_id: '任务 ID', task_type: '任务类型', workflow_graph_id: '画布 ID', node_id: '节点 ID', platform: '平台',
  platform_id: '平台 ID', model: '模型标识', model_name: '模型', progress: '进度', error_message: '错误信息',
  display_name: '显示名称', base_url: '基础地址', masked_key: 'API Key', supplier_id: '供应商 ID',
  supplier_name: '供应商', route_order: '路由顺序', resource_type: '资源类型', billing_method: '计费方式',
  price_per_unit: '单位价格', price_per_second: '每秒价格', currency: '币种',
  pic_url: '图片地址', url: '跳转地址', position: '展示位置', points: '积分', used_by_user_id: '使用用户 ID', used_time: '使用时间',
  badge_type_id: '认证类型 ID', is_official: '官方模板', city: '城市', province: '省份', district: '区县',
  agent_level: '代理级别', commission_rate: '分佣比例', recharge_commission_rate: '充值分佣比例',
  pay_amount: '支付金额', recharge_points: '充值积分', order_no: '订单号', balance: '余额', amount: '金额',
  merchant_order_id: '商户订单号', user_nickname: '用户昵称', user_mobile_masked: '用户手机号',
  subject: '订单内容', amount_yuan: '支付金额（元）', refund_amount_yuan: '退款金额（元）',
  recharge_amount_yuan: '充值金额（元）', payment_status: '支付状态', payment_method: '支付渠道',
  transaction_id: '渠道订单号', success_time: '支付成功时间',
  credited_points: '到账积分', reversed_points: '已冲正积分',
  remaining_reversible_points: '剩余可冲正', account_balance: '账户余额',
  frozen_balance: '冻结积分', available_points: '可用积分',
  max_reversible_points: '本次最多可冲正', reversal_status: '积分冲正状态',
  vip_package_name: '套餐内部名称', vip_package_display_name: '套餐名称', vip_package_pice: '套餐价格', point: '发放积分', package_days: '有效天数',
  number_concurrent_tasks: '并发任务数', owner_nickname: '所有者', owner_username: '所有者账号', owner_mobile_masked: '所有者手机号', workflow_id: '画布 ID', category: '分类',
  limited_time_activity: '限时活动配置',
  is_public: '公开', biz_type: '业务类型', refund_amount: '退款金额',
  date: '日期', count: '数量', severity: '严重级别', resource_id: '资源 ID', issue_type: '异常类型',
};

const userFields: EntityFormField[] = [
  { name: 'username', label: '用户名', required: true },
  { name: 'nickname', label: '昵称', required: true },
  { name: 'password', label: '初始密码', type: 'password', required: true },
  { name: 'mobile', label: '手机号' }, { name: 'email', label: '邮箱' },
  { name: 'status', label: '状态', type: 'select', options: statusOptions },
  { name: 'remark', label: '备注', type: 'textarea' },
];

const roleFields: EntityFormField[] = [
  { name: 'name', label: '角色名称', required: true },
  { name: 'code', label: '角色编码', required: true, placeholder: '必须以 wjs_admin_ 开头' },
  { name: 'sort', label: '排序', type: 'number' },
  { name: 'data_scope', label: '用户数据范围', type: 'select', required: true, options: [
    { label: '全部数据', value: 1 }, { label: '自定义部门', value: 2 }, { label: '本部门', value: 3 },
    { label: '本部门及下级', value: 4 }, { label: '仅本人', value: 5 },
  ] },
  { name: 'status', label: '状态', type: 'select', options: statusOptions },
  { name: 'remark', label: '备注', type: 'textarea' },
];

const menuFields: EntityFormField[] = [
  { name: 'name', label: '菜单名称', required: true },
  { name: 'permission', label: '权限编码', placeholder: '使用已登记的后台权限编码' },
  { name: 'type', label: '类型', type: 'select', required: true, options: [
    { label: '目录', value: 1 }, { label: '菜单', value: 2 }, { label: '按钮/权限', value: 3 },
  ] },
  { name: 'parent_id', label: '上级菜单 ID', type: 'number' }, { name: 'sort', label: '排序', type: 'number' },
  { name: 'path', label: '路由路径' }, { name: 'icon', label: '图标' }, { name: 'component', label: '组件键' },
  { name: 'status', label: '状态', type: 'select', options: statusOptions }, { name: 'visible', label: '可见', type: 'switch' },
];

const departmentFields: EntityFormField[] = [
  { name: 'name', label: '部门名称', required: true }, { name: 'parent_id', label: '上级部门 ID', type: 'number' },
  { name: 'sort', label: '排序', type: 'number' }, { name: 'leader_user_id', label: '负责人用户 ID', type: 'number' },
  { name: 'phone', label: '联系电话' }, { name: 'email', label: '邮箱' },
  { name: 'status', label: '状态', type: 'select', options: statusOptions },
];

const planFields: EntityFormField[] = [
  { name: 'vip_package_name', label: '套餐名称', required: true },
  { name: 'membership_scope', label: '适用域', type: 'select', required: true, options: [
    { label: '个人会员', value: 'personal' }, { label: '团队会员', value: 'team' },
  ] },
  { name: 'package_days', label: '有效天数', type: 'number', min: 1 },
  { name: 'vip_package_pice', label: '套餐价格', type: 'number', min: 0 },
  { name: 'point', label: '发放积分', type: 'number', min: 0 },
  { name: 'number_concurrent_tasks', label: '并发任务数', type: 'number', min: 1 },
  { name: 'identifying', label: '套餐标识' }, { name: 'description', label: '描述', type: 'textarea' },
  {
    name: 'limited_time_activity',
    label: '限时活动（JSON）',
    type: 'json',
    placeholder: '{"enabled":true,"title":"限时活动","highlight":{"enabled":true,"title":"Seedance 2.5 狂欢季","description":"Seedance 2.5 限时 58 折","notice":"高亮活动说明","starts_at":"2026-08-26T00:00:00+08:00","ends_at":"2026-09-30T23:59:59+08:00"},"items":[{"enabled":true,"text":"Wan 3.0系列 限时8折","notice":"Wan 3.0 专属活动时间及说明","starts_at":"2026-08-24T00:00:00+08:00","ends_at":"2026-08-28T00:00:00+08:00"},{"enabled":true,"text":"MiniMax H3 限时6折","notice":"MiniMax H3 专属活动时间及说明","starts_at":"2026-07-31T00:00:00+08:00","ends_at":"2026-09-01T00:00:00+08:00"}],"starts_at":"2026-08-26T00:00:00+08:00","ends_at":"2026-09-30T23:59:59+08:00"}',
  },
  { name: 'status', label: '状态', type: 'select', options: [{ label: '停用', value: 0 }, { label: '启用', value: 1 }] },
];

const bannerFields: EntityFormField[] = [
  { name: 'title', label: '标题', required: true }, { name: 'pic_url', label: '图片 URL', required: true },
  { name: 'url', label: '跳转地址' }, { name: 'sort', label: '排序', type: 'number' },
  { name: 'position', label: '位置', type: 'select', required: true, options: [{ label: '首页头部', value: 1 }, { label: '底部', value: 2 }] },
  { name: 'status', label: '状态', type: 'select', options: [{ label: '失效', value: 0 }, { label: '活跃', value: 1 }] },
  { name: 'memo', label: '说明', type: 'textarea' },
];

const platformFields: EntityFormField[] = [
  { name: 'name', label: '唯一标识', required: true }, { name: 'display_name', label: '显示名称', required: true },
  { name: 'base_url', label: '基础地址' },
];

const apiKeyFields: EntityFormField[] = [
  { name: 'platform_id', label: '平台 ID', type: 'number', required: true },
  { name: 'name', label: 'Key 名称', required: true }, { name: 'api_key', label: 'API Key', type: 'password', required: true },
];

const modelFields: EntityFormField[] = [
  { name: 'name', label: '显示名称', required: true }, { name: 'model', label: '模型标识', required: true },
  { name: 'supplier_id', label: '供应商 ID', type: 'number', required: true },
  { name: 'type', label: '模型类型', type: 'select', required: true, options: [
    { label: '对话', value: 1 }, { label: '图像', value: 2 }, { label: '识图', value: 4 },
    { label: '视频', value: 10 }, { label: 'TTS', value: 12 }, { label: '音乐', value: 13 },
    { label: '3D 生成', value: 14 },
  ] },
  { name: 'alias', label: '别名' }, { name: 'logical_model', label: '逻辑模型' },
  { name: 'description', label: '描述', type: 'textarea' }, { name: 'sort', label: '排序', type: 'number' },
];

const supplierFields: EntityFormField[] = [
  { name: 'name', label: '供应商名称', required: true },
  { name: 'platform_ids', label: '平台 ID 数组', type: 'json', placeholder: '[1, 2]' },
  { name: 'base_url', label: '基础地址' }, { name: 'api_key', label: 'API Key', type: 'password' },
  { name: 'weight', label: '权重', type: 'number', min: 1 }, { name: 'priority', label: '优先级', type: 'number', min: 1 },
  { name: 'connect_timeout', label: '连接超时（秒）', type: 'number', min: 1 },
  { name: 'read_timeout', label: '读取超时（秒）', type: 'number', min: 1 },
  { name: 'total_timeout', label: '总超时（秒）', type: 'number', min: 1 }, { name: 'enabled', label: '启用', type: 'switch' },
];

const routingFields: EntityFormField[] = [
  { name: 'model_id', label: '模型 ID', type: 'number', required: true },
  { name: 'supplier_id', label: '供应商 ID', type: 'number', required: true },
  { name: 'client_type', label: '客户端类型' }, { name: 'resolution', label: '分辨率' },
  { name: 'route_order', label: '路由顺序', type: 'number' },
  { name: 'override_base_url', label: '覆盖基础地址' }, { name: 'override_api_key', label: '覆盖 API Key', type: 'password' },
];

const pricingFields: EntityFormField[] = [
  { name: 'model_id', label: '模型 ID', type: 'number', required: true },
  { name: 'billing_method', label: '计费方式', type: 'select', required: true, options: [
    { label: '按次', value: 'per_unit' }, { label: '按秒', value: 'per_second' }, { label: '按 Token', value: 'per_token' },
  ] },
  { name: 'price_per_unit', label: '单位价格', type: 'number', min: 0 },
  { name: 'price_per_second', label: '每秒价格', type: 'number', min: 0 },
  { name: 'price_prompt_per_1k', label: 'Prompt 单价/1K', type: 'number', min: 0 },
  { name: 'price_completion_per_1k', label: 'Completion 单价/1K', type: 'number', min: 0 },
  { name: 'resolution_tier', label: '分辨率档位' }, { name: 'currency', label: '币种' },
  { name: 'is_active', label: '启用', type: 'switch' },
];

const cityAgentFields: EntityFormField[] = [
  { name: 'user_id', label: '用户 ID', type: 'number', required: true }, { name: 'user_name', label: '用户名称' },
  { name: 'province', label: '省份' }, { name: 'city', label: '城市', required: true }, { name: 'district', label: '区县' },
  { name: 'agent_level', label: '代理级别', type: 'select', options: [{ label: '城市合伙人', value: 1 }, { label: '超级创作者', value: 2 }] },
  { name: 'commission_rate', label: '分佣比例（%）', type: 'number', min: 0, max: 100 },
  { name: 'recharge_commission_rate', label: '充值分佣比例（%）', type: 'number', min: 0, max: 100 },
  { name: 'remark', label: '备注', type: 'textarea' },
];

export const RESOURCE_DEFINITIONS: Record<string, ResourceDefinition> = {
  '/admin/recharge': {
    labels: {
      id: '支付订单 ID',
      merchant_order_id: '商户订单号',
      transaction_id: '渠道流水号',
    },
  },
  '/admin/users': {
    labels: { avatar: '头像', account_points: '账户积分' },
    create: { permission: 'wjs-admin:user:create', title: '新增平台用户', buttonLabel: '新增用户', method: 'post', path: '/admin/users', fields: userFields, reasonRequired: true },
    edit: { permission: 'wjs-admin:user:update', title: '编辑平台用户', buttonLabel: '编辑', method: 'put', path: row => `/admin/users/${row.id}`, fields: userFields.filter(field => !['username', 'password', 'status'].includes(field.name)), reasonRequired: true },
  },
  '/admin/roles': {
    create: { permission: 'wjs-admin:role:create', title: '新增后台角色', buttonLabel: '新增角色', method: 'post', path: '/admin/roles', fields: roleFields, reasonRequired: true },
    edit: { permission: 'wjs-admin:role:update', title: '编辑后台角色', buttonLabel: '编辑', method: 'put', path: row => `/admin/roles/${row.id}`, fields: roleFields.filter(field => !['code', 'data_scope'].includes(field.name)), reasonRequired: true },
  },
  '/admin/menus': {
    create: { permission: 'wjs-admin:menu:create', title: '新增菜单或权限', buttonLabel: '新增菜单', method: 'post', path: '/admin/menus', fields: menuFields, reasonRequired: true },
    edit: { permission: 'wjs-admin:menu:update', title: '编辑菜单或权限', buttonLabel: '编辑', method: 'put', path: row => `/admin/menus/${row.id}`, fields: menuFields, reasonRequired: true },
  },
  '/admin/departments': {
    create: { permission: 'wjs-admin:department:create', title: '新增部门', buttonLabel: '新增部门', method: 'post', path: '/admin/departments', fields: departmentFields, reasonRequired: true },
    edit: { permission: 'wjs-admin:department:update', title: '编辑部门', buttonLabel: '编辑', method: 'put', path: row => `/admin/departments/${row.id}`, fields: departmentFields, reasonRequired: true },
  },
  '/admin/posts': {
    create: { permission: 'wjs-admin:post:create', title: '新增岗位', buttonLabel: '新增岗位', method: 'post', path: '/admin/posts', fields: [
      { name: 'code', label: '岗位编码', required: true }, { name: 'name', label: '岗位名称', required: true },
      { name: 'sort', label: '排序', type: 'number' }, { name: 'status', label: '状态', type: 'select', options: statusOptions },
      { name: 'remark', label: '备注', type: 'textarea' },
    ], reasonRequired: true },
  },
  '/admin/membership-plans': {
    create: { permission: 'wjs-admin:membership-plan:create', title: '新增会员套餐', buttonLabel: '新增套餐', method: 'post', path: '/admin/membership-plans', fields: planFields, reasonRequired: true },
    edit: { permission: 'wjs-admin:membership-plan:update', title: '编辑会员套餐', buttonLabel: '编辑', method: 'put', path: row => `/admin/membership-plans/${row.id}`, fields: planFields, reasonRequired: true },
  },
  '/admin/banners': {
    create: { permission: 'wjs-admin:banner:create', title: '新增 Banner', buttonLabel: '新增 Banner', method: 'post', path: '/admin/banners', fields: bannerFields, reasonRequired: true },
    edit: { permission: 'wjs-admin:banner:update', title: '编辑 Banner', buttonLabel: '编辑', method: 'put', path: row => `/admin/banners/${row.id}`, fields: bannerFields, reasonRequired: true },
  },
  '/admin/redeem-codes': {
    create: { permission: 'wjs-admin:redeem-code:create', title: '批量生成兑换码', buttonLabel: '生成兑换码', method: 'post', path: '/admin/redeem-codes', fields: [
      { name: 'points', label: '每个兑换码积分', type: 'number', required: true, min: 1 },
      { name: 'count', label: '生成数量', type: 'number', required: true, min: 1, max: 1000 },
      { name: 'description', label: '说明', type: 'textarea' },
    ], reasonRequired: true },
  },
  '/admin/platforms': {
    create: { permission: 'wjs-admin:platform:create', title: '新增 AI 平台', buttonLabel: '新增平台', method: 'post', path: '/admin/platforms', fields: platformFields },
    edit: { permission: 'wjs-admin:platform:update', title: '编辑 AI 平台', buttonLabel: '编辑', method: 'put', path: row => `/admin/platforms/${row.id}`, fields: platformFields.filter(field => field.name !== 'name') },
  },
  '/admin/api-keys': {
    create: { permission: 'wjs-admin:api-key:create', title: '新增 API Key', buttonLabel: '新增 Key', method: 'post', path: '/admin/api-keys', fields: apiKeyFields },
    edit: { permission: 'wjs-admin:api-key:update', title: '编辑 API Key', buttonLabel: '编辑', method: 'put', path: row => `/admin/api-keys/${row.id}`, fields: apiKeyFields.filter(field => field.name !== 'platform_id').map(field => field.name === 'api_key' ? { ...field, required: false } : field) },
  },
  '/admin/models': {
    create: { permission: 'wjs-admin:model:create', title: '新增模型', buttonLabel: '新增模型', method: 'post', path: '/admin/models', fields: modelFields },
    edit: { permission: 'wjs-admin:model:update', title: '编辑模型', buttonLabel: '编辑', method: 'put', path: row => `/admin/models/${row.id}`, fields: modelFields.filter(field => field.name !== 'supplier_id') },
  },
  '/admin/suppliers': {
    create: { permission: 'wjs-admin:supplier:create', title: '新增供应商', buttonLabel: '新增供应商', method: 'post', path: '/admin/suppliers', fields: supplierFields },
    edit: { permission: 'wjs-admin:supplier:update', title: '编辑供应商', buttonLabel: '编辑', method: 'put', path: row => `/admin/suppliers/${row.id}`, fields: supplierFields },
  },
  '/admin/routing': {
    create: { permission: 'wjs-admin:routing:create', title: '新增路由规则', buttonLabel: '新增路由', method: 'post', path: '/admin/routing', fields: routingFields },
    edit: { permission: 'wjs-admin:routing:update', title: '编辑路由规则', buttonLabel: '编辑', method: 'put', path: row => `/admin/routing/${row.id}`, fields: routingFields.filter(field => !['model_id', 'supplier_id'].includes(field.name)) },
  },
  '/admin/pricing-rules': {
    create: { permission: 'wjs-admin:pricing-rule:create', title: '新增计价规则', buttonLabel: '新增计价', method: 'post', path: '/admin/pricing-rules', fields: pricingFields },
    edit: { permission: 'wjs-admin:pricing-rule:update', title: '编辑计价规则', buttonLabel: '编辑', method: 'put', path: row => `/admin/pricing-rules/${row.id}`, fields: pricingFields },
  },
  '/admin/city-agents': {
    create: { permission: 'wjs-admin:city-agent:create', title: '新增城市代理', buttonLabel: '新增代理', method: 'post', path: '/admin/city-agents', fields: cityAgentFields },
    edit: { permission: 'wjs-admin:city-agent:update', title: '编辑城市代理', buttonLabel: '编辑', method: 'put', path: row => `/admin/city-agents/${row.id}`, fields: cityAgentFields.filter(field => field.name !== 'user_id') },
  },
};

export function endpointBase(endpoint: string): string {
  return endpoint.split('?')[0];
}

export function labelForField(field: string, definition?: ResourceDefinition): string {
  return definition?.labels?.[field] ?? commonLabels[field] ?? field;
}
